import React, { useState, useEffect } from 'react';
import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';
import xor from 'lodash/xor';
import { Field } from '@atlaskit/form';
import { Box, xcss } from '@atlaskit/primitives';
import { CheckboxSelect } from '@atlaskit/select';
import Tooltip from '@atlaskit/tooltip';
import ViewTracker from '@atlassian/jira-analytics-web-react/src/components/view-tracker.tsx';
import type { AssetType } from '@atlassian/jira-global-asset-types/src/types.tsx';
import { useIntl } from '@atlassian/jira-intl';
import type { FieldConfigurationComponentProps } from '../../../../types.tsx';
import type { AssociatedField } from '../../../../types/layout-items.tsx';
import TestIdWrapper from '../../common/test-wrapper/index.tsx';
import { AkFieldWrapper } from '../../styled.tsx';
import messages from './messages.tsx';

export type Props = FieldConfigurationComponentProps & {
	readonly assetTypes: AssetType[];
	readonly assetFetchError: Error | undefined;
};

export const hasAssetFieldConfiguration = (field: AssociatedField): boolean =>
	!!field.configuration && !!field.configuration.assetTypes;

export const getSelectedAssetTypesFromField = (field: AssociatedField): AssetType[] => {
	if (!field || !field.configuration || !field.configuration.assetTypes) {
		return [];
	}
	// @ts-expect-error - TS2322 - Type 'unknown' is not assignable to type 'AssetType[]'.
	return field.configuration.assetTypes;
};

export const getSelectedAssetTypesStillInSync = (
	selectedAssetTypes: AssetType[],
	assetTypes: AssetType[],
): AssetType[] =>
	selectedAssetTypes.filter((selectedAssetType) =>
		assetTypes.find((assetType) => isEqual(assetType, selectedAssetType)),
	);

export const shouldUpdateAssetTypesConfiguration = (
	field: AssociatedField,
	selectedAssetTypes: AssetType[],
): boolean =>
	(!hasAssetFieldConfiguration(field) && !isEmpty(selectedAssetTypes)) ||
	(hasAssetFieldConfiguration(field) &&
		!isEmpty(xor(getSelectedAssetTypesFromField(field), selectedAssetTypes)));

export const AssetTypeFieldConfiguration = ({
	field,
	onUpdate,
	assetTypes,
	assetFetchError,
	tooltipDelay,
	testId,
}: Props) => {
	const { formatMessage } = useIntl();

	const [selectedAssetTypes, setSelectedAssetTypes] = useState<AssetType[]>(
		getSelectedAssetTypesFromField(field),
	);
	useEffect(() => {
		if (shouldUpdateAssetTypesConfiguration(field, selectedAssetTypes)) {
			onUpdate({
				...field,
				configuration: {
					...field.configuration,
					assetTypes: selectedAssetTypes,
				},
			});
		}
	});

	const isModifyPropertyConfigurationDisabled = !field.operations.canModifyPropertyConfiguration;

	const hasAssetTypesOrError = () => !isEmpty(assetTypes) || !!assetFetchError;

	const getNoOptionsMessage = (): string => formatMessage(messages.noAssetTypes);

	const getOptionValue = (assetType: AssetType): string =>
		`${assetType.origin.appKey}/${assetType.origin.originId}`;

	const getOptionLabel = (assetType: AssetType): string => assetType.label.value;

	const onChange = (changedSelectedAssetTypes: AssetType[] | null): void => {
		// Adding null check due to https://ecosystem.atlassian.net/jira/servicedesk/projects/DS/queues/issue/DS-7451
		setSelectedAssetTypes(changedSelectedAssetTypes || []);
	};

	// Removes the out of sync selected asset types that are no longer returned from the saffron service
	const removeOutOfSyncAssetTypesFromConfiguration = (): void => {
		const selectedAssetTypesStillInSync = getSelectedAssetTypesStillInSync(
			selectedAssetTypes,
			assetTypes,
		);

		if (!assetFetchError && selectedAssetTypesStillInSync.length !== selectedAssetTypes.length) {
			setSelectedAssetTypes(selectedAssetTypesStillInSync);
		}
	};

	const render = () => {
		removeOutOfSyncAssetTypesFromConfiguration();

		return (
			<Box xcss={assetsDefaultValueStyledStyles}>
				<AkFieldWrapper>
					<Field
						name={`${field.key}-default-value`}
						label={formatMessage(messages.assetTypesLabel)}
					>
						{({ fieldProps }) => (
							<Tooltip
								content={
									isModifyPropertyConfigurationDisabled
										? formatMessage(messages.assetTypesDisabledTooltip)
										: null
								}
								delay={tooltipDelay}
							>
								<TestIdWrapper testId={testId}>
									<CheckboxSelect
										inputId={fieldProps.id}
										placeholder={formatMessage(messages.assetTypesPlaceholder)}
										noOptionsMessage={getNoOptionsMessage}
										// @ts-expect-error - CheckboxSelect does not allow specifying generics so we cannot type this
										getOptionValue={getOptionValue}
										// @ts-expect-error - CheckboxSelect does not allow specifying generics so we cannot type this
										getOptionLabel={getOptionLabel}
										// @ts-expect-error - CheckboxSelect does not allow specifying generics so we cannot type this
										onChange={onChange}
										// @ts-expect-error - CheckboxSelect does not allow specifying generics so we cannot type this
										value={selectedAssetTypes}
										isDisabled={isModifyPropertyConfigurationDisabled}
										// @ts-expect-error - CheckboxSelect does not allow specifying generics so we cannot type this
										options={assetTypes}
									/>
								</TestIdWrapper>
							</Tooltip>
						)}
					</Field>
				</AkFieldWrapper>
			</Box>
		);
	};

	return hasAssetTypesOrError() ? (
		<>
			<ViewTracker key={field.key} />
			{render()}
		</>
	) : null;
};

const assetsDefaultValueStyledStyles = xcss({
	paddingRight: 'space.500',
	paddingLeft: 'space.500',
	marginBottom: 'space.200',
});
